/**
 * 67.54: the explosion look (the owner, Oct 18: "still super perf cheap, but better explosion animation / design"), built
 * on lane C's classes (constants/explosions.ts, ARCHITECTURE §5.21; the `blast` event carries its `explosion`).
 *
 * Every class draws the same parts, sized and timed its own way (`EXPLOSION_LOOKS`, people):
 * - **flash** — a white-hot core, there on the first frame, gone in a tenth of a second or two;
 * - **fireball** — a few hot puffs swelling and cooling from yellow through orange to dark red;
 * - **smoke** — puffs that rise, drift and grow; the bomb, the shells and a vehicle's death raise a column;
 * - **debris** — chunks of the ground (its material's colour; a vehicle's are dark metal) thrown up and falling back;
 * - **dust** — a ring of the ground's colour pushed out along it;
 * - **scorch** — a dark mark left on the ground, fading after a while: draped on the drawn ground under the burst (the
 *   owner, 67.54's bug: "there's a little black circle that shows up on the ground, and it's always perfectly flat and at
 *   the height of the explosion, so it doesn't match the terrain and if you shoot a truck the circle floats above the
 *   terrain and the point of impact"); a burst high off the ground (on a hull, a wall, a wreck) leaves none (`scorchShare`).
 *
 * Cheap by construction: ONE `ExplosionField` holds six instanced meshes (one per part), every explosion's pieces are
 * slots in them, so the scene draws six meshes however many bursts are going (`EXPLOSION_DRAWS`). The fire is additive
 * (a slot fades to black: invisible); the smoke and the dust shrink away; a dead slot is zero-scaled. No per-burst
 * allocation, no materials made or disposed in play; the caps (`POOL`) drop the oldest piece of a kind when full.
 *
 * `detail` thins a burst for distance (the far view folds into the diorama's look, 68.14): `full` near; `mid` no debris and
 * half the dust; `far` the flash, the fireball and the smoke only, the smoke a little larger to read from above.
 *
 * Rendering only: reads nothing of the sim; seeded per burst so a sheet shows the same burst each time.
 */
import * as THREE from 'three';
import type { GroundMaterial, Vec3 } from '../types';
import type { ExplosionClass } from '../constants';
import { markShared } from './meshBake';

export type BurstDetail = 'full' | 'mid' | 'far';

/** A class's look (sizes in people, times in seconds). */
export interface BurstLook {
  flash: { r: number; t: number };
  fireball: { r: number; t: number; puffs: number };
  smoke: { puffs: number; r: number; rise: number; t: number; color: string; column?: boolean };
  debris: { n: number; speed: number; size: number; metal?: boolean };
  dust: { n: number; r: number; t: number };
  scorch: { r: number; t: number };
}

export const EXPLOSION_LOOKS: Record<ExplosionClass, BurstLook> = {
  /** the LAW: a sharp flash, a short fireball, grey smoke, a spray of ground */
  law: { flash: { r: 0.9, t: 0.12 }, fireball: { r: 1.1, t: 0.45, puffs: 5 }, smoke: { puffs: 7, r: 0.65, rise: 3, t: 4.5, color: '#8c8782' }, debris: { n: 12, speed: 5, size: 0.12 }, dust: { n: 9, r: 2.4, t: 1.6 }, scorch: { r: 1.1, t: 30 } },
  /** 65.43: the Panzerfaust's warhead — the LAW's look, a little larger, until 67.54 gives it its own */
  panzerfaust: { flash: { r: 1.0, t: 0.12 }, fireball: { r: 1.1, t: 0.45, puffs: 5 }, smoke: { puffs: 7, r: 0.65, rise: 3, t: 4.5, color: '#8c8782' }, debris: { n: 12, speed: 5, size: 0.12 }, dust: { n: 9, r: 2.4, t: 1.6 }, scorch: { r: 1.1, t: 30 } },
  /** the 40 mm: smaller than the LAW's */
  gl40: { flash: { r: 0.7, t: 0.1 }, fireball: { r: 0.85, t: 0.38, puffs: 4 }, smoke: { puffs: 5, r: 0.5, rise: 2.4, t: 3.5, color: '#8a857f' }, debris: { n: 9, speed: 4.2, size: 0.1 }, dust: { n: 7, r: 1.9, t: 1.4 }, scorch: { r: 0.85, t: 25 } },
  /** DECISIONS 277 (2): the 20 mm's round — a spark of a flash and a puff, under half the 40 mm's */
  cannon20: { flash: { r: 0.4, t: 0.06 }, fireball: { r: 0.35, t: 0.18, puffs: 2 }, smoke: { puffs: 2, r: 0.3, rise: 1.4, t: 1.8, color: '#8a857f' }, debris: { n: 4, speed: 3.2, size: 0.07 }, dust: { n: 3, r: 0.9, t: 0.9 }, scorch: { r: 0.4, t: 12 } },
  /** the HE grenade: more dust and dirt than fire */
  handGrenade: { flash: { r: 0.6, t: 0.08 }, fireball: { r: 0.6, t: 0.25, puffs: 3 }, smoke: { puffs: 6, r: 0.55, rise: 2.2, t: 3.5, color: '#958a7c' }, debris: { n: 14, speed: 4.6, size: 0.1 }, dust: { n: 10, r: 2.2, t: 1.8 }, scorch: { r: 0.9, t: 25 } },
  /** the strike drone's charge: the grenade's, a little more fire (it goes off at the drone) */
  strikeCharge: { flash: { r: 0.7, t: 0.1 }, fireball: { r: 0.75, t: 0.3, puffs: 4 }, smoke: { puffs: 6, r: 0.55, rise: 2.4, t: 3.5, color: '#8a857f' }, debris: { n: 12, speed: 4.6, size: 0.1 }, dust: { n: 10, r: 2.2, t: 1.8 }, scorch: { r: 0.9, t: 25 } },
  /** the airstrike's bomb: a big fireball and a column */
  bomb: { flash: { r: 2.2, t: 0.16 }, fireball: { r: 2.8, t: 0.9, puffs: 9 }, smoke: { puffs: 16, r: 1.3, rise: 10, t: 10, color: '#77726d', column: true }, debris: { n: 26, speed: 8, size: 0.18 }, dust: { n: 16, r: 6, t: 2.6 }, scorch: { r: 3, t: 60 } },
  /** the gun's shell: a fireball the size the shells draw today (≈ 2.7 people at k 1/20), a column of smoke */
  shell: { flash: { r: 1.5, t: 0.14 }, fireball: { r: 2.7, t: 0.7, puffs: 8 }, smoke: { puffs: 12, r: 1.1, rise: 7, t: 8, color: '#7b7671', column: true }, debris: { n: 20, speed: 7, size: 0.16 }, dust: { n: 14, r: 4.5, t: 2.2 }, scorch: { r: 2.4, t: 45 } },
  /** the heavy shell (≈ 4.4 people) */
  heavyShell: { flash: { r: 2.2, t: 0.16 }, fireball: { r: 4.4, t: 0.85, puffs: 10 }, smoke: { puffs: 16, r: 1.5, rise: 10, t: 10, color: '#77726d', column: true }, debris: { n: 26, speed: 8.5, size: 0.2 }, dust: { n: 16, r: 6.5, t: 2.6 }, scorch: { r: 3.4, t: 60 } },
  /** 69.29: the tank's explosive round — the LAW's look, a little larger, until lane E gives it its own */
  tankHe: { flash: { r: 1.05, t: 0.12 }, fireball: { r: 1.2, t: 0.5, puffs: 6 }, smoke: { puffs: 8, r: 0.7, rise: 3.2, t: 5, color: '#8c8782' }, debris: { n: 14, speed: 5.5, size: 0.12 }, dust: { n: 10, r: 2.6, t: 1.7 }, scorch: { r: 1.2, t: 30 } },
  /** a vehicle's death (DECISIONS 195: modest): a fuel fireball, slow and orange, metal thrown, a black column that burns on */
  vehicleDeath: { flash: { r: 1.2, t: 0.14 }, fireball: { r: 2, t: 1.2, puffs: 8 }, smoke: { puffs: 18, r: 1.1, rise: 12, t: 14, color: '#2e2a27', column: true }, debris: { n: 10, speed: 5, size: 0.2, metal: true }, dust: { n: 8, r: 3, t: 1.8 }, scorch: { r: 2.6, t: 90 } },
  /** 69.47: the engineer's placed charge — a sharp, white-hot flash and a hard push of dust and debris, little fire (C4 burns clean) */
  charge: { flash: { r: 1.3, t: 0.1 }, fireball: { r: 1.0, t: 0.3, puffs: 4 }, smoke: { puffs: 9, r: 0.8, rise: 3.6, t: 5.5, color: '#9a948c' }, debris: { n: 22, speed: 7, size: 0.13 }, dust: { n: 14, r: 3.2, t: 2 }, scorch: { r: 1.0, t: 30 } },
};
export const EXPLOSION_CLASSES =Object.keys(EXPLOSION_LOOKS) as ExplosionClass[];

/** The ground's colour, for the dust and the debris thrown up. */
export const GROUND_DUST: Record<GroundMaterial, string> = {
  soil: '#6b5638', rock: '#7a7670', sand: '#c4ab7c', snow: '#e6ecf0', ice: '#b9d2df', mud: '#4a3a28', ash: '#5a5550', salt: '#ddd8cc',
};
const METAL = '#2b2d30', SCORCH = '#1a1714';

/** How many pieces each part keeps at most (the oldest is reused past it). */
export const POOL = { flash: 24, fireball: 128, smoke: 320, debris: 400, dust: 192, scorch: 96 } as const;
export type ExplosionPart = keyof typeof POOL;
type Part = ExplosionPart;
/** The scene's draws for every explosion at once: one instanced mesh per part. */
export const EXPLOSION_DRAWS = Object.keys(POOL).length;

/** 69.3b, the ship's crash: one piece set going by `ExplosionField.emit` (world units; colours as three takes them). */
export interface PieceSpec {
  at: Vec3; vel?: Vec3; up: Vec3; ground?: Vec3;
  life: number; s0: number; s1: number;
  c0: THREE.ColorRepresentation; c1?: THREE.ColorRepresentation;
  drag?: number; gravity?: number; delay?: number; spin?: number; flat?: boolean;
}

/** One piece in flight: where, how it moves, how long, its size over its life, its colour over its life. */
interface Piece {
  alive: boolean;
  pos: THREE.Vector3; vel: THREE.Vector3; up: THREE.Vector3; ground: THREE.Vector3;
  age: number; life: number; delay: number;
  s0: number; s1: number;
  c0: THREE.Color; c1: THREE.Color;
  gravity: number; drag: number; spin: THREE.Vector3; rot: THREE.Euler;
  flat: boolean;
}

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

const EMBER = new THREE.Color('#c2410c');
const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _s = new THREE.Vector3(), _c = new THREE.Color(), _qUp = new THREE.Quaternion();
const Y = new THREE.Vector3(0, 1, 0);

class Pool {
  readonly mesh: THREE.InstancedMesh;
  readonly pieces: Piece[] = [];
  private next = 0;
  constructor(scene: THREE.Scene, readonly part: Part, geometry: THREE.BufferGeometry, material: THREE.Material) {
    const n = POOL[part];
    this.mesh = new THREE.InstancedMesh(geometry, material, n);
    this.mesh.name = `explosions:${part}`;   // (every instanced layer named: a census or a picked object says which)
    this.mesh.frustumCulled = false;   // the pieces move anywhere on the planet
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    for (let i = 0; i < n; i++) {
      this.pieces.push({ alive: false, pos: new THREE.Vector3(), vel: new THREE.Vector3(), up: new THREE.Vector3(0, 1, 0), ground: new THREE.Vector3(), age: 0, life: 1, delay: 0, s0: 0, s1: 0, c0: new THREE.Color(), c1: new THREE.Color(), gravity: 0, drag: 0, spin: new THREE.Vector3(), rot: new THREE.Euler(), flat: false });
      this.mesh.setMatrixAt(i, _m.makeScale(0, 0, 0));
      this.mesh.setColorAt(i, _c.set(0, 0, 0));
    }
    this.mesh.renderOrder = part === 'scorch' ? 1 : part === 'smoke' || part === 'dust' ? 3 : 4;
    scene.add(this.mesh);
  }
  /** A slot: the next free one, else the oldest (round-robin). */
  take(): Piece {
    const n = this.pieces.length;
    for (let k = 0; k < n; k++) { const i = (this.next + k) % n; if (!this.pieces[i].alive) { this.next = (i + 1) % n; return this.pieces[i]; } }
    const p = this.pieces[this.next]; this.next = (this.next + 1) % n; return p;
  }
  get live(): number { let n = 0; for (const p of this.pieces) if (p.alive) n++; return n; }
}

/**
 * The drawn ground under a world point: writes the ground's point along the planet's up through `p` into `out` (the host's
 * drawn ground, fine relief and all); false where there is none. A burst given one drapes its scorch on it and lands its
 * debris there.
 */
export type GroundUnder = (p: THREE.Vector3, out: THREE.Vector3) => boolean;

/**
 * How much of a burst's scorch the ground takes, `height` above the drawn ground for a scorch of `radius` (world units): all
 * of it within a quarter of its radius of the ground, none from its radius up (a burst on a hull, a wall or a wreck: a
 * scorch on a hull is the vehicle's damage look, not the ground's), shrinking between.
 */
export function scorchShare(height: number, radius: number): number {
  if (!(radius > 0)) return 0;
  const h = Math.max(0, height);
  return h <= 0.25 * radius ? 1 : h >= radius ? 0 : 1 - (h - 0.25 * radius) / (0.75 * radius);
}

/** A scorch's disc: a centre and two rings of SCORCH_SEG (the outer one its soft edge). */
const SCORCH_SEG = 12, SCORCH_VERTS = 1 + 2 * SCORCH_SEG;
const _g = new THREE.Vector3(), _t1 = new THREE.Vector3(), _t2 = new THREE.Vector3(), _v = new THREE.Vector3();

/**
 * The scorches: one merged mesh (one draw) of POOL.scorch draped discs. A disc's 25 vertices are laid once, at its burst, on
 * the drawn ground (`GroundUnder`) — so it follows a slope and the fine relief — and its fade is its vertices' alpha.
 */
class ScorchPool {
  readonly mesh: THREE.Mesh;
  private readonly pos: THREE.BufferAttribute;
  private readonly col: THREE.BufferAttribute;
  private readonly base = new Float32Array(POOL.scorch * SCORCH_VERTS);
  readonly slots = Array.from({ length: POOL.scorch }, () => ({ alive: false, age: 0, life: 1 }));
  private next = 0;
  constructor(scene: THREE.Scene, material: THREE.Material) {
    const n = POOL.scorch, g = new THREE.BufferGeometry(), index: number[] = [];
    this.pos = new THREE.BufferAttribute(new Float32Array(n * SCORCH_VERTS * 3), 3).setUsage(THREE.DynamicDrawUsage);
    this.col = new THREE.BufferAttribute(new Float32Array(n * SCORCH_VERTS * 4), 4).setUsage(THREE.DynamicDrawUsage);
    for (let k = 0; k < n; k++) {
      const o = k * SCORCH_VERTS;
      for (let i = 0; i < SCORCH_SEG; i++) {
        const j = (i + 1) % SCORCH_SEG, a = o + 1 + i, b = o + 1 + j, c = o + 1 + SCORCH_SEG + i, d = o + 1 + SCORCH_SEG + j;
        index.push(o, b, a, a, b, d, a, d, c);
      }
    }
    g.setIndex(index); g.setAttribute('position', this.pos); g.setAttribute('color', this.col);
    this.mesh = new THREE.Mesh(g, material);
    this.mesh.name = 'explosions:scorch';
    this.mesh.frustumCulled = false;   // the marks lie anywhere on the planet
    this.mesh.renderOrder = 1;
    scene.add(this.mesh);
  }
  get live(): number { let n = 0; for (const s of this.slots) if (s.alive) n++; return n; }
  /**
   * A scorch of `radius` centred on `centre` (on the ground), its up `up`, turned `turn`, living `life` s: its rings laid out
   * along the ground's tangents and each vertex dropped (or raised) onto the drawn ground by `ground`, then `lift` over it.
   */
  add(centre: THREE.Vector3, up: THREE.Vector3, radius: number, life: number, color: THREE.Color, turn: number, ground: GroundUnder | undefined, lift: number): void {
    let k = -1;
    for (let q = 0; q < this.slots.length; q++) { const i = (this.next + q) % this.slots.length; if (!this.slots[i].alive) { k = i; break; } }
    if (k < 0) k = this.next;
    this.next = (k + 1) % this.slots.length;
    Object.assign(this.slots[k], { alive: true, age: 0, life });
    _t1.crossVectors(up, Math.abs(up.y) < 0.9 ? Y : _v.set(1, 0, 0)).normalize(); _t2.crossVectors(up, _t1);
    const o = k * SCORCH_VERTS;
    const put = (vi: number, x: THREE.Vector3, alpha: number) => {
      if (ground && ground(x, _g)) x.copy(_g);
      x.addScaledVector(up, lift);
      this.pos.setXYZ(o + vi, x.x, x.y, x.z);
      this.base[o + vi] = alpha;
      this.col.setXYZW(o + vi, color.r, color.g, color.b, alpha);
    };
    put(0, _v.copy(centre), 1);
    for (let ring = 0; ring < 2; ring++) for (let i = 0; i < SCORCH_SEG; i++) {
      const a = turn + (i / SCORCH_SEG) * Math.PI * 2, r = radius * (ring === 0 ? 0.62 : 1);
      put(1 + ring * SCORCH_SEG + i, _v.copy(centre).addScaledVector(_t1, Math.cos(a) * r).addScaledVector(_t2, Math.sin(a) * r), ring === 0 ? 0.9 : 0);
    }
    this.pos.needsUpdate = true; this.col.needsUpdate = true;
  }
  /** Age the scorches; the last fifth of a life fades its alpha out. */
  update(dt: number): void {
    let touched = false;
    this.slots.forEach((s, k) => {
      if (!s.alive) return;
      s.age += dt;
      const f = s.age >= s.life ? 0 : s.age / s.life < 0.8 ? -1 : 1 - (s.age / s.life - 0.8) / 0.2;
      if (f < 0) return;
      if (f === 0) s.alive = false;
      const o = k * SCORCH_VERTS;
      for (let v = 0; v < SCORCH_VERTS; v++) this.col.setW(o + v, this.base[o + v] * f);
      touched = true;
    });
    if (touched) this.col.needsUpdate = true;
  }
  dispose(): void { this.mesh.geometry.dispose(); }
}

/** All explosions' pieces: five instanced meshes and the scorches' one mesh, updated once a frame. `person` is world units a person (1.8 on a sheet, the person set's height in play). */
export class ExplosionField {
  private readonly pools: Record<Exclude<Part, 'scorch'>, Pool>;
  private readonly scorch: ScorchPool;
  constructor(private readonly scene: THREE.Scene, private readonly person: number) {
    const shared = <T extends THREE.Material | THREE.BufferGeometry>(x: T) => markShared(x);
    const additive = shared(new THREE.MeshBasicMaterial({ transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, toneMapped: false }));
    // soft: smooth-shaded rounded puffs, see-through (a faceted, opaque puff read as a boulder)
    const smokeMat = shared(new THREE.MeshLambertMaterial({ transparent: true, opacity: 0.42, depthWrite: false }));
    const dustMat = shared(new THREE.MeshLambertMaterial({ transparent: true, opacity: 0.34, depthWrite: false }));
    const solid = shared(new THREE.MeshLambertMaterial({ flatShading: true }));
    const scorchMat = shared(new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.55, depthWrite: false, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }));
    const ico1 = shared(new THREE.IcosahedronGeometry(1, 1));
    this.pools = {
      flash: new Pool(scene, 'flash', ico1, additive),
      fireball: new Pool(scene, 'fireball', ico1, additive),
      smoke: new Pool(scene, 'smoke', ico1, smokeMat),
      debris: new Pool(scene, 'debris', shared(new THREE.BoxGeometry(1, 1, 1)), solid),
      dust: new Pool(scene, 'dust', ico1, dustMat),
    };
    this.scorch = new ScorchPool(scene, scorchMat);
  }

  /** Pieces alive now (for a sheet's count and the cost probe). */
  get live(): number { return (Object.values(this.pools) as Pool[]).reduce((n, p) => n + p.live, 0) + this.scorch.live; }

  /**
   * A burst of `cls` at `at` on ground whose up is `up`, of `material` (its dust and its debris), thinned for distance by
   * `detail`. `seed` makes it the same burst each time (a sheet); play passes the event's own number. `ground`, the drawn
   * ground (play's): the debris lands on it and the scorch lies draped on it — none from a burst high above it; without it,
   * the plane through the burst (a sheet).
   */
  spawn(cls: ExplosionClass, at: Vec3, up: Vec3, material: GroundMaterial, detail: BurstDetail = 'full', seed = 1, ground?: GroundUnder): void {
    const L = EXPLOSION_LOOKS[cls], P = this.person, rnd = mulberry32(seed * 2654435761 + 7);
    const u = new THREE.Vector3(up.x, up.y, up.z).normalize(), o = new THREE.Vector3(at.x, at.y, at.z);
    // two tangents along the ground
    const t1 = new THREE.Vector3().crossVectors(u, Math.abs(u.y) < 0.9 ? Y : new THREE.Vector3(1, 0, 0)).normalize(), t2 = new THREE.Vector3().crossVectors(u, t1);
    const along = (a: number, r: number) => t1.clone().multiplyScalar(Math.cos(a) * r).addScaledVector(t2, Math.sin(a) * r);
    const dirt = GROUND_DUST[material] ?? GROUND_DUST.soil;
    // the drawn ground under the burst (its own point without one) and how high the burst stands over it
    const floor = ground && ground(o, _g) ? _g.clone() : o.clone(), height = _v.copy(o).sub(floor).dot(u);
    const set = (pool: Exclude<Part, 'scorch'>, f: (p: Piece) => void) => { const p = this.pools[pool].take(); p.alive = true; p.age = 0; p.delay = 0; p.gravity = 0; p.drag = 0; p.flat = false; p.spin.set(0, 0, 0); p.rot.set(0, 0, 0); p.up.copy(u); p.ground.copy(o); f(p); };
    // the flash: white-hot, on the first frame
    set('flash', p => { p.pos.copy(o).addScaledVector(u, 0.3 * L.flash.r * P); p.vel.set(0, 0, 0); p.life = L.flash.t; p.s0 = L.flash.r * P; p.s1 = L.flash.r * P * 1.3; p.c0.set('#fff6e0'); p.c1.set('#000000'); });
    // the fireball: hot puffs swelling out and up, cooling yellow → orange → dark
    for (let i = 0; i < L.fireball.puffs; i++) set('fireball', p => {
      const a = rnd() * Math.PI * 2, r = (0.15 + 0.35 * rnd()) * L.fireball.r * P;
      p.pos.copy(o).addScaledVector(u, (0.25 + 0.35 * rnd()) * L.fireball.r * P).add(along(a, r * 0.4));
      p.vel.copy(along(a, r * 1.2 / L.fireball.t)).addScaledVector(u, (0.4 + 0.6 * rnd()) * L.fireball.r * P / L.fireball.t);
      p.drag = 3; p.life = L.fireball.t * (0.7 + 0.5 * rnd()); p.delay = 0.03 * rnd();
      p.s0 = 0.25 * L.fireball.r * P; p.s1 = (0.45 + 0.25 * rnd()) * L.fireball.r * P;
      p.c0.set(cls === 'vehicleDeath' ? '#ffb347' : '#ffd27a'); p.c1.set('#000000');
    });
    // the smoke: puffs rising and spreading, a column for the big ones; larger and fewer from far off
    const puffs = detail === 'far' ? Math.ceil(L.smoke.puffs * 0.6) : L.smoke.puffs, grow = detail === 'far' ? 1.35 : 1;
    for (let i = 0; i < puffs; i++) set('smoke', p => {
      const a = rnd() * Math.PI * 2, f = i / Math.max(1, puffs - 1);
      const r = (L.smoke.column ? 0.25 : 0.6) * L.smoke.r * P * rnd();
      p.pos.copy(o).add(along(a, r)).addScaledVector(u, 0.4 * L.smoke.r * P);
      const rise = L.smoke.rise * P * (L.smoke.column ? 0.35 + 0.65 * f : 0.45 + 0.55 * rnd());
      p.drag = 0.7;   // up fast, slowing: it reaches most of its rise in a second or two
      p.vel.copy(along(a, 0.5 * L.smoke.r * P)).addScaledVector(u, rise * p.drag);
      p.life = L.smoke.t * (0.6 + 0.4 * rnd()); p.delay = 0.04 + (L.smoke.column ? 0.45 * f : 0.2 * rnd());
      p.s0 = 0.6 * L.smoke.r * P * grow; p.s1 = (1.7 + 0.9 * rnd()) * L.smoke.r * P * grow;
      p.c0.set(L.smoke.color).multiplyScalar(0.55); p.c1.set(L.smoke.color).lerp(_c.set('#b4b0aa'), 0.25 * rnd());   // sooty, lightening as it rises
      p.spin.set(0.2 * rnd(), 0.4 * (rnd() - 0.5), 0);
    });
    if (detail === 'far') return;
    // the debris: chunks of the ground thrown up, tumbling, falling back onto it
    if (detail === 'full') for (let i = 0; i < L.debris.n; i++) set('debris', p => {
      const a = rnd() * Math.PI * 2, out = 0.3 + 0.7 * rnd(), sp = L.debris.speed * P * (0.5 + 0.6 * rnd());
      p.pos.copy(o).addScaledVector(u, 0.1 * P);
      p.vel.copy(along(a, sp * out)).addScaledVector(u, sp * (0.7 + 0.5 * rnd()));
      p.gravity = 9 * P; p.drag = 0.2; p.life = 1.4 + 1.2 * rnd(); p.ground.copy(floor);   // (it lands on the drawn ground, not the burst's height)
      p.s0 = p.s1 = L.debris.size * P * (0.6 + 0.8 * rnd());
      p.c0.set(L.debris.metal ? METAL : dirt).multiplyScalar(0.75 + 0.3 * rnd()); p.c1.copy(p.c0);
      p.spin.set(8 * (rnd() - 0.5), 8 * (rnd() - 0.5), 8 * (rnd() - 0.5));
    });
    // the dust: the ground's colour pushed out along it in a ring, settling
    const dust = detail === 'mid' ? Math.ceil(L.dust.n / 2) : L.dust.n;
    for (let i = 0; i < dust; i++) set('dust', p => {
      const a = (i / dust) * Math.PI * 2 + 0.4 * rnd();
      p.pos.copy(o).addScaledVector(u, 0.15 * P);
      p.vel.copy(along(a, L.dust.r * P * 1.8 / L.dust.t)).addScaledVector(u, 0.25 * P);
      p.drag = 2.2; p.life = L.dust.t * (0.8 + 0.4 * rnd());
      p.s0 = 0.35 * P; p.s1 = (0.8 + 0.4 * rnd()) * L.dust.r * P / 3;
      p.c0.set(dirt).lerp(_c.set('#c9c3b8'), 0.3); p.c1.copy(p.c0).lerp(_c.set('#d8d2c8'), 0.25);
      p.flat = true;
    });
    // the scorch: a dark mark draped on the drawn ground under the burst, as much of it as the burst's height leaves (none from a hull)
    const r = L.scorch.r * P * (0.9 + 0.2 * rnd()), turn = rnd() * Math.PI * 2, share = scorchShare(height, L.scorch.r * P);
    if (share > 0) this.scorch.add(floor, u, r * share, L.scorch.t, _c.set(SCORCH), turn, ground, 0.03 * P);
  }

  /**
   * 69.3b, the ship's crash: one piece of `part` set going on its own — a falling ship's flame and trail, a wreck's fire and
   * smoke, the crash's skid of dust and metal — in the same pools as the bursts (no draw of its own). `e` is read at once (a
   * caller may reuse it): sizes in world units, `up` the piece's up (debris falls against it to the plane through `ground`).
   */
  emit(part: Part, e: PieceSpec): void {
    if (part === 'scorch') { this.scorch.add(_g.set(e.at.x, e.at.y, e.at.z), _v.set(e.up.x, e.up.y, e.up.z).normalize().clone(), e.s0, e.life, _c.set(e.c0), 0, undefined, 0.03 * this.person); return; }
    const p = this.pools[part].take();
    p.alive = true; p.age = 0; p.delay = e.delay ?? 0; p.flat = !!e.flat; p.rot.set(0, 0, 0);
    p.pos.set(e.at.x, e.at.y, e.at.z); p.vel.set(e.vel?.x ?? 0, e.vel?.y ?? 0, e.vel?.z ?? 0);
    p.up.set(e.up.x, e.up.y, e.up.z); p.ground.set(e.ground?.x ?? e.at.x, e.ground?.y ?? e.at.y, e.ground?.z ?? e.at.z);
    p.life = e.life; p.s0 = e.s0; p.s1 = e.s1; p.gravity = e.gravity ?? 0; p.drag = e.drag ?? 0;
    p.c0.set(e.c0); p.c1.set(e.c1 ?? e.c0);
    p.spin.set(e.spin ?? 0, (e.spin ?? 0) * 0.7, (e.spin ?? 0) * 0.4);
  }

  /** Advance every piece `dt` seconds and write the instances (the only per-frame work). */
  update(dt: number): void {
    for (const pool of Object.values(this.pools) as Pool[]) {
      let touched = false;
      pool.pieces.forEach((p, i) => {
        if (!p.alive) return;
        touched = true;
        if (p.delay > 0) { p.delay -= dt; this.write(pool, i, p, 0); return; }
        p.age += dt;
        const k = p.age / p.life;
        if (k >= 1) { p.alive = false; pool.mesh.setMatrixAt(i, _m.makeScale(0, 0, 0)); return; }
        if (p.gravity) p.vel.addScaledVector(p.up, -p.gravity * dt);
        if (p.drag) p.vel.multiplyScalar(Math.exp(-p.drag * dt));
        p.pos.addScaledVector(p.vel, dt);
        if (p.gravity) {   // debris lands and stops on the ground (its plane through the burst)
          const h = _s.copy(p.pos).sub(p.ground).dot(p.up);
          if (h < 0) { p.pos.addScaledVector(p.up, -h); p.vel.set(0, 0, 0); p.spin.set(0, 0, 0); p.gravity = 0; }
        }
        p.rot.x += p.spin.x * dt; p.rot.y += p.spin.y * dt; p.rot.z += p.spin.z * dt;
        this.write(pool, i, p, k);
      });
      if (touched) { pool.mesh.instanceMatrix.needsUpdate = true; if (pool.mesh.instanceColor) pool.mesh.instanceColor.needsUpdate = true; }
    }
    this.scorch.update(dt);
  }

  private write(pool: Pool, i: number, p: Piece, k: number): void {
    const ease = 1 - (1 - k) * (1 - k);
    let size = p.s0 + (p.s1 - p.s0) * ease;
    const part = pool.part;
    if (part === 'smoke' || part === 'dust') size *= k < 0.75 ? 1 : 1 - (k - 0.75) / 0.25 * 0.85;   // shrinks away at the end
    if (part === 'debris' && k > 0.85) size *= 1 - (k - 0.85) / 0.15;
    if (p.delay > 0) size = 0;
    _qUp.setFromUnitVectors(Y, p.up);
    _q.setFromEuler(p.rot); _q.premultiply(_qUp);
    _s.set(size, p.flat ? size * 0.5 : size, size);
    pool.mesh.setMatrixAt(i, _m.compose(p.pos, _q, _s));
    // the fire fades to black (additive: gone); everything else keeps its colour (it shrinks instead)
    if (part === 'flash' || part === 'fireball') {
      const heat = part === 'fireball' ? (k < 0.3 ? 1 : 1 - (k - 0.3) / 0.7) : 1 - k;
      _c.copy(p.c0);
      if (part === 'fireball') _c.lerp(EMBER, Math.min(1, k * 1.6));
      pool.mesh.setColorAt(i, _c.multiplyScalar(Math.max(0, heat)));
    } else pool.mesh.setColorAt(i, _c.copy(p.c0).lerp(p.c1, k));
  }

  dispose(): void {
    for (const pool of Object.values(this.pools) as Pool[]) { this.scene.remove(pool.mesh); pool.mesh.dispose(); }
    this.scene.remove(this.scorch.mesh); this.scorch.dispose();
  }
}

/** The detail a burst `people` from the camera is drawn at: full near, no debris past 60 people, flash, fire and smoke only past 200 (the diorama's distances). */
export const BURST_DETAIL = { mid: 60, far: 200 } as const;
export function burstDetail(people: number): BurstDetail { return people > BURST_DETAIL.far ? 'far' : people > BURST_DETAIL.mid ? 'mid' : 'full'; }
